import type { FileRecord } from "@cohesive/api-client";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { prepareSrcDocHtml } from "../../../lib/htmlIframeSrcDoc";
import { getPresignedUrlExpiresAtMs } from "../../../lib/presignedUrl";
import { cn } from "../../../lib/utils";
import { Markdown } from "../../Markdown/Markdown";
import { HtmlBlockShield } from "./HtmlBlockShield";
import { HtmlCardPlaceholder } from "./HtmlCardPlaceholder";
import { useHtmlCardSrcDoc } from "./useHtmlCardSrcDoc";
import {
  useIframeMountAdmission,
  type IframeMountPolicy,
} from "./useIframeMountAdmission";

/** Module-level so the pipeline hook's delivery effect sees a stable
 *  identity — the document well has no render options to vary. The frame
 *  takes no pointer events, so locking its scroll loses nothing and keeps
 *  WebKit from compositing the canvas viewport (see `lockScroll`). */
const PREPARE_DOCUMENT = (html: string) =>
  prepareSrcDocHtml(html, { colorScheme: "light", lockScroll: true });

/** Page width the document lays out at before scaling to the well — the
 *  preview pipeline's document viewport (8.5in at 96dpi), so the card and
 *  its poster share one composition. */
export const DOCUMENT_PAGE_WIDTH = 816;

/**
 * Lays its child out at `DOCUMENT_PAGE_WIDTH` and scales it to fill the
 * well's width (origin top-left; the page's visible height is the well's
 * height at that scale). Vector content stays vector: text is rasterised by
 * the browser at the final on-screen size, whatever the canvas zoom.
 */
function PageScaler({ children }: { children: ReactNode }) {
  const wellRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = wellRef.current;
    if (!el) return;
    const apply = (w: number) => {
      if (w > 0) setScale(w / DOCUMENT_PAGE_WIDTH);
    };
    apply(el.clientWidth);
    const observer = new ResizeObserver(([entry]) => {
      if (entry) apply(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={wellRef} className="absolute inset-0 overflow-hidden">
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{
          width: DOCUMENT_PAGE_WIDTH,
          height: `${100 / scale}%`,
          transform: `scale(${scale})`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export type DocumentCardWellProps = Pick<
  FileRecord,
  "url" | "path" | "updated_at"
> & {
  /** `text/html` or `text/markdown` (parameters stripped). */
  contentType: string;
  /** Async rewrite applied to html before display — swaps embedded public
   *  file URLs for presigned ones (the sandbox sends no credentials). */
  transformHtml?: (html: string) => Promise<string> | string;
  /** `"progressive"` paces the iframe mount through the shared admission
   *  scheduler (public share view); default `"eager"`. */
  mountPolicy?: IframeMountPolicy;
  /** Server-rendered poster shown until the live document paints. */
  previewImageUrl?: string | null;
};

/**
 * The well of a document card on the canvas: the document itself, live and
 * inert, laid out at the well's own width.
 *
 * Documents are read in the detail pane; on the canvas they only need to
 * be recognisable and legible, and text does that best as text — vector
 * at every zoom in every browser, where a bitmap poster is at the mercy of
 * each engine's scaler. So:
 *
 *   - html renders in a sandboxed iframe (scripts allowed: many documents
 *     need them to lay out) that never takes pointer events; the shield
 *     above it keeps select / double-click / drag on the node;
 *   - markdown renders as DOM through the same `Markdown` component the
 *     detail pane uses, so the card and the pane agree on how it reads.
 *
 * Both lay out at the page width the preview pipeline renders at (816 CSS
 * px — 8.5in at 96dpi) and scale to the well, so the card reads as a page
 * thumbnail: the same composition as the poster, but vector. The poster the
 * pipeline still generates is for the rail, the public placeholder and the
 * publish snapshot.
 *
 * Memoised: the canvas node re-renders on every zoom tick (it subscribes
 * to the viewport for its chrome), and the markdown well parses on render.
 * Every prop here is a primitive or a stable callback, so the well only
 * re-renders when the document itself changes.
 */
export const DocumentCardWell = memo(function DocumentCardWell({
  url,
  path,
  updated_at,
  contentType,
  transformHtml,
  mountPolicy = "eager",
  previewImageUrl = null,
}: DocumentCardWellProps) {
  const isMarkdown = contentType === "text/markdown";
  return (
    <div
      className="relative w-full flex-1 min-h-0 overflow-hidden bg-white"
      style={{ colorScheme: "light" }}
    >
      {isMarkdown ? (
        <MarkdownWell url={url} updated_at={updated_at} />
      ) : (
        <HtmlWell
          url={url}
          path={path}
          updated_at={updated_at}
          transformHtml={transformHtml}
          mountPolicy={mountPolicy}
          previewImageUrl={previewImageUrl ?? null}
        />
      )}
      <HtmlBlockShield />
    </div>
  );
});

function HtmlWell({
  url,
  path,
  updated_at,
  transformHtml,
  mountPolicy,
  previewImageUrl,
}: Omit<DocumentCardWellProps, "contentType" | "previewImageUrl"> & {
  mountPolicy: IframeMountPolicy;
  previewImageUrl: string | null;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const { displayDoc } = useHtmlCardSrcDoc({
    url,
    updated_at,
    transformHtml,
    optionsSalt: "",
    prepare: PREPARE_DOCUMENT,
  });
  // `ready` keeps this card out of the scheduler until its document can
  // actually mount, so an admission slot always paces a real iframe.
  const { admitted, onIframeLoad } = useIframeMountAdmission({
    policy: mountPolicy,
    hostRef,
    ready: displayDoc !== null,
  });
  const [isVisible, setIsVisible] = useState(false);
  const handleIframeLoad = useCallback(() => {
    setIsVisible(true);
    onIframeLoad();
  }, [onIframeLoad]);
  // Swap from the placeholder only when the frame actually remounts. The
  // swap is instant: an opacity transition inside the canvas viewport
  // composites it in WebKit and blurs every node for its duration.
  const mountKey = displayDoc?.mountKey ?? null;
  useEffect(() => {
    if (mountKey) setIsVisible(false);
  }, [mountKey]);

  const title = (path && path.split("/").pop()) || "HTML";
  return (
    <div ref={hostRef} className="absolute inset-0">
      {!isVisible && (
        <HtmlCardPlaceholder previewImageUrl={previewImageUrl} showPlate />
      )}
      <PageScaler>
        {displayDoc && admitted && (
          <iframe
            key={displayDoc.mountKey}
            srcDoc={displayDoc.srcDoc}
            className={cn(
              "h-full w-full border-0",
              isVisible ? "opacity-100" : "opacity-0",
            )}
            style={{ pointerEvents: "none", colorScheme: "light" }}
            sandbox="allow-scripts"
            title={title}
            onLoad={handleIframeLoad}
          />
        )}
      </PageScaler>
    </div>
  );
}

function MarkdownWell({
  url,
  updated_at,
}: Pick<DocumentCardWellProps, "url" | "updated_at">) {
  const [text, setText] = useState<string | null>(null);
  // Keyed like `useHtmlCardSrcDoc`: rotation alone never refetches — the
  // stable S3 path plus `updated_at` for content, plus the URL's expiry so a
  // card that mounted on a dead presign re-reads when the fresh one lands.
  const urlRef = useRef(url);
  urlRef.current = url;
  const s3Path = useMemo(() => {
    if (!url) return null;
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  }, [url]);
  const urlExpiresAtMs = useMemo(() => getPresignedUrlExpiresAtMs(url), [url]);
  useEffect(() => {
    const target = urlRef.current;
    if (!target) return;
    let cancelled = false;
    void fetch(target)
      .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
      .then((body) => {
        if (!cancelled) setText(body);
      })
      .catch(() => {
        // Keep existing content on error — don't go blank.
      });
    return () => {
      cancelled = true;
    };
  }, [s3Path, updated_at, urlExpiresAtMs]);
  // Parsed once per text: react-markdown walks the whole document on render.
  const rendered = useMemo(
    () =>
      text != null ? (
        <Markdown className="max-w-[800px] mx-auto">{text}</Markdown>
      ) : null,
    [text],
  );

  return (
    <PageScaler>
      {/* Same composition as the pipeline's markdown page template (800px
          column, 40px top margin, 16px/1.6, #333) with 56px side margins so
          markdown reads with page margins the way an html document does. */}
      <div
        className="h-full select-none pointer-events-none overflow-hidden"
        style={{
          padding: "40px 56px 0",
          fontSize: 16,
          lineHeight: 1.6,
          color: "#333",
          fontFamily:
            'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
        aria-hidden
      >
        {rendered}
      </div>
    </PageScaler>
  );
}
